import { posthog } from 'posthog-js';
import { usePosthogEvent } from './usePosthogEvent';

export const usePosthogCommonEvents = () => {
  const { trackEvent } = usePosthogEvent();

  // Chat events
  const trackChatEvent = (action: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'chat_action',
      properties: {
        action,
        ...properties,
      },
    });
  };

  // File events
  const trackFileEvent = (action: string, filePath: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'file_action',
      properties: {
        action,
        file_path: filePath,
        ...properties,
      },
    });
  };

  // GitHub events
  const trackGithubEvent = (action: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'github_action',
      properties: {
        action,
        ...properties,
      },
    });
  };

  // Deployment events
  const trackDeploymentEvent = (action: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'deployment_action',
      properties: {
        action,
        ...properties,
      },
    });
  };

  // Supabase events
  const trackSupabaseEvent = (action: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'supabase_action',
      properties: {
        action,
        ...properties,
      },
    });
  };

  // Project events
  const trackProjectEvent = (action: string, properties?: Record<string, any>) => {
    trackEvent({
      name: 'project_action',
      properties: {
        action,
        ...properties,
      },
    });
  };

  // Template events (clicked, iterated, downloaded, published)
  const trackTemplateEvent = (
    action: 'clicked' | 'iterated' | 'downloaded' | 'published',
    properties?: Record<string, any>,
  ) => {
    const eventName = `template_${action}` as const;
    trackEvent({
      name: eventName,
      properties: {
        activation_channel: 'templates',
        ...properties,
      },
    });
  };

  // Activation events
  const trackActivatedUser = (channel: 'chat' | 'figma' | 'github' | 'templates', properties?: Record<string, any>) => {
    trackEvent({
      name: 'project_created',
      properties: {
        activation_channel: channel,
        ...properties,
      },
    });

    // Store activation channel on the person for breakdowns
    try {
      posthog.people.set({
        activation_channel: channel,
      });
    } catch (error) {
      console.error('[PostHog] Failed to set activation_channel:', error);
    }
  };

  // Retention events (message usage)
  const trackMessageSent = (properties?: Record<string, any>) => {
    trackEvent({
      name: 'message_sent',
      properties: {
        ...properties,
      },
    });
  };

  // Booking / Calendly events
  const trackCallBooked = (source?: string) => {
    trackEvent({
      name: 'call_booked',
      properties: {
        source: source || 'unknown',
      },
    });
  };

  // manage subscription events
  const trackManageSubscription = (action: string) => {
    trackEvent({
      name: 'manage_subscription',
      properties: {
        action,
      },
    });
  };

  // canvas mode toggle
  const trackCanvasInteraction = (action: Record<string, any>) => {
    trackEvent({
      name: 'canvas_interaction',
      properties: {
        ...action,
      },
    });
  };

  const trackBugReport = (properties?: Record<string, any>) => {
    trackEvent({
      name: 'bug_report',
      properties: {
        ...properties,
      },
    });
  };

  return {
    trackChatEvent,
    trackFileEvent,
    trackGithubEvent,
    trackDeploymentEvent,
    trackSupabaseEvent,
    trackProjectEvent,
    trackTemplateEvent,
    trackActivatedUser,
    trackMessageSent,
    trackCallBooked,
    trackManageSubscription,
    trackCanvasInteraction,
    trackBugReport,
  };
};

export default usePosthogCommonEvents;
